import { A11yModule } from '@angular/cdk/a11y';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CdkStepperModule } from '@angular/cdk/stepper';
import { CdkTableModule } from '@angular/cdk/table';
import { CdkTreeModule } from '@angular/cdk/tree';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';
import { RouterModule } from '@angular/router';
import { TranslationModule } from '@translation';

import { MaterialAccordionComponent } from './components/accordion/accordion.component';
import { MaterialButtonComponent } from './components/button/button.component';
import { MaterialCheckboxComponent } from './components/checkbox/checkbox.component';
import { MaterialFileUploadComponent } from './components/file-upload/file-upload.component';
import { MaterialInputComponent } from './components/input/input.component';
import { MaterialInputDateComponent } from './components/inputdate/inputdate.component';
import { MaterialInputPasswordComponent } from './components/inputpassword/inputpassword.component';
import { MarkdownCommentInputComponent } from './components/markdown-comment-input/markdown-comment-input.component';
import { MaterialMenuButtonComponent } from './components/menu-button/menu-button.component';
import { MaterialPaginatorComponent } from './components/paginator/paginator.component';
import { MaterialRadioGroupComponent } from './components/radio-group/radio-group.component';
import { MaterialSelectComponent } from './components/select/select.component';
import { MaterialSelectGroupComponent } from './components/selectgroup/selectgroup.component';
import { MaterialSlidetoggleComponent } from './components/slidetoggle/slidetoggle.component';
import { MaterialTextAreaComponent } from './components/textarea/textarea.component';
import { UploadComponent } from './components/upload/upload.component';
import { TableVirtualScrollViewportComponent } from './components/virtual-scroll-wrapper/table-virtual-scroll-viewport.component';
import { AutofocusDirective } from './directives/autofocus/autofocus.directive';
import { DebounceDirective } from './directives/debounce/debounce.directive';
import { FileDragDetectorDirective } from './directives/file-drag-detector/file-drag-detector.directive';
import { FileDragDropDirective } from './directives/file-drag-drop/file-drag-drop.directive';
import { TailwindMaterialDirective } from './directives/tailwind-material/tailwind-material.directive';
import { FilterPipe } from './pipes/filter/filter.pipe';
import { PascalToCamelPipe } from './pipes/pascal-to-camel/pascal-to-camel.pipe';
import { TruncateHtmlTagsPipe } from './pipes/truncate-html/truncate-html-tags.pipe';
import { FileUpload } from './services/file-upload/fileuploadservice';

const materialModules = [
    MatAutocompleteModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatCheckboxModule,
    MatDialogModule,
    MatDividerModule,
    MatExpansionModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatSortModule,
    MatTableModule,
    MatTabsModule,
    MatTooltipModule,
    MatSnackBarModule,
    MatToolbarModule,
    MatStepperModule,
    MatDatepickerModule,
    MatPaginatorModule,
    MatNativeDateModule,
    MatListModule,
    MatBadgeModule,
    MatBottomSheetModule,
    MatChipsModule,
    MatGridListModule,
    MatProgressBarModule,
    MatRippleModule,
    MatSidenavModule,
    MatSliderModule,
    MatTreeModule,
    MatFormFieldModule,
    A11yModule,
    DragDropModule,
    CdkStepperModule,
    CdkTableModule,
    CdkTreeModule,
    ScrollingModule,
];

@NgModule({
    declarations: [
        MaterialAccordionComponent,
        MaterialButtonComponent,
        MaterialMenuButtonComponent,
        MaterialCheckboxComponent,
        MaterialInputComponent,
        MaterialSelectComponent,
        MaterialFileUploadComponent,
        MaterialInputDateComponent,
        MaterialInputPasswordComponent,
        MarkdownCommentInputComponent,
        MaterialPaginatorComponent,
        MaterialRadioGroupComponent,
        MaterialSelectGroupComponent,
        MaterialSlidetoggleComponent,
        MaterialTextAreaComponent,
        UploadComponent,
        TableVirtualScrollViewportComponent,
        DebounceDirective,
        TailwindMaterialDirective,
        FileDragDetectorDirective,
        FileDragDropDirective,
        AutofocusDirective,
        FilterPipe,
        PascalToCamelPipe,
        TruncateHtmlTagsPipe,
    ],
    imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, TranslationModule, ...materialModules],
    providers: [{ provide: 'FileUpload', useExisting: FileUpload }],
    exports: [
        ...materialModules,
        MaterialAccordionComponent,
        MaterialButtonComponent,
        MaterialMenuButtonComponent,
        MaterialCheckboxComponent,
        MaterialInputComponent,
        MaterialSelectComponent,
        MaterialFileUploadComponent,
        MaterialInputDateComponent,
        MaterialInputPasswordComponent,
        MarkdownCommentInputComponent,
        MaterialPaginatorComponent,
        MaterialRadioGroupComponent,
        MaterialSelectGroupComponent,
        MaterialSlidetoggleComponent,
        MaterialTextAreaComponent,
        TableVirtualScrollViewportComponent,
        UploadComponent,
        DebounceDirective,
        TailwindMaterialDirective,
        FileDragDetectorDirective,
        FileDragDropDirective,
        AutofocusDirective,
        FilterPipe,
        PascalToCamelPipe,
        TruncateHtmlTagsPipe,
    ],
})
export class MaterialModule {}
